import viacep from "@/api/viacep";
import Swal from "sweetalert2";

export function formatCurrency(value) {
  const currencyValue = parseCurrencyToNumber(value);
  if (currencyValue === null) return "";

  return (
    "R$ " +
    currencyValue
      .toFixed(2)
      .replace(".", ",")
      .replace(/(\d)(?=(\d{3})+(?!\d))/g, "$1.")
  );
}

export function parseCurrency(value) {
  const number = parseCurrencyToNumber(value);
  return number === null ? "" : number.toFixed(2);
}

export function parseCurrencyToNumber(value) {
  if (typeof value === "number") return Number.isFinite(value) ? value : null;
  if (typeof value !== "string") return null;

  let cleaned = value.replace(/R\$/g, "").replace(/\s/g, "");
  if (!/^-?\d+(?:[.,]\d+)*$/.test(cleaned)) return null;

  const comma = cleaned.lastIndexOf(",");
  const dot = cleaned.lastIndexOf(".");
  if (comma !== -1 && dot !== -1) {
    // O último separador é decimal em valores como 1.234,56 e 1,234.56.
    const decimal = comma > dot ? "," : ".";
    const grouping = decimal === "," ? /\./g : /,/g;
    cleaned = cleaned.replace(grouping, "").replace(decimal, ".");
  } else if (/^-?\d{1,3}(?:\.\d{3})+$/.test(cleaned) || /^-?\d{1,3}(?:,\d{3})+$/.test(cleaned)) {
    // Sem centavos, grupos de três dígitos representam milhares.
    cleaned = cleaned.replace(/[.,]/g, "");
  } else {
    cleaned = cleaned.replace(",", ".");
  }

  const number = Number(cleaned);
  return Number.isFinite(number) ? number : null;
}

export function handleInput(value) {
  if (!value) return "0,00";

  // Remove tudo que não for número
  const digitsOnly = value.replace(/\D/g, "");

  // Converte para centavos e formata com vírgula
  const numericValue = (parseFloat(digitsOnly) / 100).toFixed(2);

  // Retorna no formato brasileiro esperado pelo formatCurrency
  return numericValue.replace(".", ","); // ← Isso é o essencial!
}

export function formatDecimal(value) {
  const cleanedValue = value.replace(/[^\d,]/g, "").replace(",", ".");
  const decimalValue = parseFloat(cleanedValue);

  if (isNaN(decimalValue)) {
    return "";
  }
  return decimalValue
    .toFixed(2)
    .replace(".", ",")
    .replace(/(\d)(?=(\d{3})+(?!\d))/g, "$1.");
}

export function parseDecimal(value) {
  if (!value) return "";
  const number = value.replace(/\./g, "").replace(",", ".");
  const parsedValue = parseFloat(number);
  return isNaN(parsedValue) ? "" : parsedValue.toFixed(2);
}

export async function validarCampoCep(
  obj,
  { tipoEndereco = null, somenteValidar = false } = {},
) {
  const cepField = obterNomeCampo("Cep", tipoEndereco);
  const cep = String(obj[cepField] ?? "").replace(/\D/g, "");

  if (cep.length !== 8) {
    if (!somenteValidar) {
      mostarErroCep();
      limparCamposEndereco(obj, tipoEndereco);
    }
    return false;
  }

  if (!somenteValidar) {
    const logradouroField = obterNomeCampo("Logradouro", tipoEndereco);
    const bairroField = obterNomeCampo("Bairro", tipoEndereco);
    const cidadeField = obterNomeCampo("Cidade", tipoEndereco);
    const ufField = obterNomeCampo("Uf", tipoEndereco);
    const paisField = obterNomeCampo("Pais", tipoEndereco);

    obj[cepField] = cep;

    try {
      const { data } = await viacep.get(`/ws/${cep}/json/`);
      if (data.erro) {
        mostarErroCep();
        limparCamposEndereco(obj, tipoEndereco);
        return false;
      } else {
        obj[logradouroField] = data.logradouro;
        obj[bairroField] = data.bairro;
        obj[cidadeField] = data.localidade;
        obj[ufField] = data.uf;
        obj[paisField] = "BRA";
        return true;
      }
    } catch (error) {
      mostarErroCep();
      limparCamposEndereco(obj, tipoEndereco);
      return false;
    }
  } else {
    try {
      const { data } = await viacep.get(`/ws/${cep}/json/`);
      if (data.erro) {
        return false;
      } else {
        return true;
      }
    } catch (error) {
      return false;
    }
  }
}

function obterNomeCampo(base, tipoEndereco) {
  if (!tipoEndereco) return base;
  const tipoEnderecoLower = tipoEndereco.toLowerCase();
  if (tipoEnderecoLower === "outro") {
    return `${base}${capitalize(tipoEndereco)}`;
  } else if (tipoEnderecoLower === "enderecocomercial") {
    switch (base) {
      case "Logradouro":
        return "EnderecoComercial";
      case "Uf":
        return `${capitalize(tipoEndereco)}Estado`;
      default:
        return `${capitalize(tipoEndereco)}${base}`;
    }
  } else {
    return base;
  }
}

function capitalize(str) {
  return str.charAt(0).toUpperCase() + str.slice(1);
}

function limparCamposEndereco(obj, tipoEndereco = null) {
  const logradouroField = obterNomeCampo("Logradouro", tipoEndereco);
  const bairroField = obterNomeCampo("Bairro", tipoEndereco);
  const cidadeField = obterNomeCampo("Cidade", tipoEndereco);
  const ufField = obterNomeCampo("Uf", tipoEndereco);
  const paisField = obterNomeCampo("Pais", tipoEndereco);

  obj[logradouroField] = "";
  obj[bairroField] = "";
  obj[cidadeField] = "";
  obj[ufField] = "";
  obj[paisField] = "";
}

function mostarErroCep() {
  Swal.fire({
    icon: "warning",
    title: "Atenção!",
    text: `O CEP informado não foi encontrado. Verifique e tente novamente ou preencha os campos manualmente. Para CEP's fora do Brasil, o preenchimento manual é necessário.`,
  });
}

export const ufs = [
  "AC",
  "AL",
  "AP",
  "AM",
  "BA",
  "CE",
  "DF",
  "ES",
  "GO",
  "MA",
  "MT",
  "MS",
  "MG",
  "PA",
  "PB",
  "PR",
  "PE",
  "PI",
  "RJ",
  "RN",
  "RS",
  "RO",
  "RR",
  "SC",
  "SP",
  "SE",
  "TO",
];
